import { IProfileDto } from '@api/types/auth/IProfileDto.ts';
import { useLang } from '@common/context/language-provider';
import { REGEX } from '@common/utils/form-validation';
import { Button } from '@konova/ui/button';
import { FormikFieldWrapper } from '@konova/ui/formik-field-wrapper';
import { Icon } from '@konova/ui/icon';
import { InfoBox } from '@konova/ui/info-box';
import { InputLabel } from '@konova/ui/input-label';
import { Modal } from '@konova/ui/modal';
import { Radio } from '@konova/ui/radio';
import { RadioGroup } from '@konova/ui/radio-group';
import { RequireConfirmation } from '@konova/ui/require-confirmation';
import { Stack } from '@konova/ui/stack';
import { TextInput } from '@konova/ui/text-input';
import { Typography } from '@konova/ui/typography';
import { SurveyRoutes } from '@routes/survey-routes.ts';
import { Field, Form, Formik } from 'formik';
import i18next from 'i18next';
import { isValidPhoneNumber } from 'libphonenumber-js';
import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { generatePath, useNavigate } from 'react-router';
import { IResponse, ISingleSignOns, ISpaceUser, ISurvey, IUser } from 'src/types';
import * as yup from 'yup';
import { FALLBACK_LANGUAGE, SALUTATIONS } from '../../constants.ts';
import { SpaceUserService } from '../../services/SpaceUserService.ts';
import { SurveyService } from '../../services/SurveyService.ts';
import { UserService } from '../../services/UserService.ts';
import { useMailNotVerifiedCountContext } from '@common/context/email-not-verified-count-context/email-not-verified-count-context.tsx';

type IVisibleFields = {
  nickname: boolean;
  phone: boolean;
};

const formSchema = (visibleFields: IVisibleFields, simplified: boolean, urlLang: string) => yup.object().shape({
  salutation: simplified ? yup.string() : yup.string().required(
    i18next.t('components.validation-message.field-required', {
      fieldName: i18next.t('common.salutation'),
      suffix: i18next.t('common.suffix.female')
    })
  ),
  nickname: visibleFields.nickname ? yup.string().required(
    i18next.t('components.validation-message.field-required', {
      fieldName: i18next.t('common.nickname2'),
      suffix: i18next.t('common.suffix.objective')
    })
  ) : yup.mixed().optional(),
  firstname: simplified ? yup.string() : yup.string().required(
    i18next.t('components.validation-message.field-required', {
      fieldName: urlLang === FALLBACK_LANGUAGE ? i18next.t('common.firstname2') : i18next.t('common.lastname'),
      suffix: i18next.t('common.suffix.objective')
    })
  ),
  lastname: simplified ? yup.string() : yup.string().required(
    i18next.t('components.validation-message.field-required', {
      fieldName: urlLang === FALLBACK_LANGUAGE ? i18next.t('common.lastname2') : i18next.t('common.lastname'),
      suffix: i18next.t('common.suffix.objective')
    })
  ),
  email: yup.string().required(
    i18next.t('components.validation-message.field-required', {
      fieldName: urlLang === FALLBACK_LANGUAGE ? i18next.t('common.email2') : i18next.t('common.email'),
      suffix: i18next.t('common.suffix.female')
    })
  ).matches(REGEX.email, i18next.t('components.validation-message.email')),
  phone: visibleFields.phone ? yup.string().required(
    i18next.t('components.validation-message.field-required', {
      fieldName: i18next.t('common.mobile-phone-number'),
      suffix: i18next.t('common.suffix.female')
    })
  ).test('is-valid-e164', i18next.t('components.validation-message.phone'), value => isValidPhoneNumber(value || '')) : yup.mixed().optional()
});

interface IProps {
  handleClose: () => void;
  logout: () => void;
  spaceUser: ISpaceUser & { email_to_verify?: string };
  user: IUser;
  loadUser: (callBack?: (err: IResponse) => void) => Promise<IUser>;
  isSimplifiedRegistrationActive: boolean;
  emailVerificationActivated?: boolean;
  handleRequest: (requestPromise: Promise<undefined>, suppressGlobalErrorToast?: boolean) => Promise<IResponse>;
  twoFaActivated: boolean;
  isAdminInAnySurvey: boolean;
  survey?: ISurvey | null;
  urlLang: string;
  onNewPhone: (phone: string) => void;
}

const AccountModal = ({
  handleClose,
  spaceUser,
  survey,
  user,
  loadUser,
  logout,
  isSimplifiedRegistrationActive,
  emailVerificationActivated,
  handleRequest,
  twoFaActivated,
  isAdminInAnySurvey,
  urlLang,
  onNewPhone,
}: IProps) => {
  const { t } = useTranslation();
  const mailNotVerifiedCountContext = useMailNotVerifiedCountContext();
  const { language: lang } = useLang();
  const [isLoading, setLoading] = useState<boolean>(false);
  const [ssoProviders, setSsoProviders] = useState<ISingleSignOns[]>([]);
  const [error, setError] = useState<string | null>(null);
  const navigate = useNavigate();

  useEffect(() => {
    handleGetSSOProviders();
  }, []);

  const visibleFields: IVisibleFields = useMemo(() => ({
    nickname: isSimplifiedRegistrationActive,
    phone: isAdminInAnySurvey && twoFaActivated
  }), [isSimplifiedRegistrationActive, isAdminInAnySurvey, twoFaActivated]);

  const handleGetSSOProviders = () => {
    if (user?.spaceUser && user.spaceUser?.hasSSO) {
      handleRequest(
        UserService.singleSignOns(user?.spaceUser.id)
      ).then(res => {
        setSsoProviders(res.body.data);
      });
    }
  };

  const handleRemoveSSOProvider = (id: number) => {
    UserService.removeSingleSignOnProvider(id, {
      space_user_id: user?.spaceUser?.id
    }).then(res => {
      if (res.statusCode === 204) {
        handleGetSSOProviders();
      }
    });
  };

  const handleSubmit = (values: IProfileDto) => {
    setLoading(true);
    setError(null);

    if (survey?.id) {
      SurveyService.show(survey.id).then(res => {
        const localSurvey = res.body.data;
        updateUserData(values, localSurvey, localSurvey?.userRole !== 'participant');
      });
    } else {
      updateUserData(values, survey, true);
    }
  };

  const updateUserData = (values: IProfileDto, localSurvey?: ISurvey | null, isAdmin: boolean = false) => {
    handleRequest(
      SpaceUserService.update({
        ...values,
        email: values.email?.toLowerCase(),
        // @ts-expect-error unknown type mismatch
        survey_id: localSurvey?.id
      }),
      true
    )
      .then(() => {
        if (twoFaActivated && values.phone && values.phone !== user?.phone && isAdmin) {
          onNewPhone(values.phone);
        }
        handleClose();
        return loadUser();
      })
      .then(updatedUser => {
        if (updatedUser?.email !== user?.email) {
          mailNotVerifiedCountContext.set(0);
        }

        if (localSurvey?.slug && !updatedUser?.email_verified_at && updatedUser?.email !== updatedUser?.email_to_verify) {
          localStorage.setItem('showEmailNotVerifiedCount', JSON.stringify(0));
          navigate(generatePath(SurveyRoutes.EmailNotVVerified, { lang, surveySlug: localSurvey.slug }));
        }
      })
      .catch(res => {
        if (res.statusCode === 422) {
          Object.keys(res.body.errors).map(function (field) {
            setError(t<string>(res.body.errors[field][0]));
          });
        } else if (res.statusCode === 409) {
          setError(res.body.reason);
        } else if (res.statusCode !== 429) {
          setError(t<string>('common.error-appeared'));
        }
      })
      .finally(() => {
        setLoading(false);
      });
  };

  return (
    <Formik<IProfileDto>
      initialValues={{
        email: spaceUser.email_to_verify || spaceUser.email || '',
        phone: user.phone || '',
        firstname: spaceUser.firstname || '',
        lastname: spaceUser.lastname || '',
        salutation: spaceUser.salutation || '',
        nickname: spaceUser.nickname || '',
        id: spaceUser.id
      }}
      onSubmit={handleSubmit}
      enableReinitialize
      validationSchema={formSchema(visibleFields, isSimplifiedRegistrationActive, urlLang)}
    >
      {({ submitForm, values }) => (
        <Modal
          open
          onClose={handleClose}
          title={t<string>('components.account.title')}
          label={t<string>('components.account.title')}
          className="account-modal"
          footer={(
            <Stack align="spread" fill>
              <div>
                <Button
                  variant="text"
                  onClick={logout}
                  prefix={<Icon icon={['far', 'sign-out']} />}
                  disabled={isLoading}
                >
                  {t<string>('common.sign-out')}
                </Button>
              </div>

              <Stack>
                <Button variant="text" onClick={handleClose} disabled={isLoading}>
                  {t<string>('common.cancel')}
                </Button>

                <Button
                  onClick={submitForm}
                  type="submit"
                  prefix={<Icon icon={['far', 'check']} />}
                  loading={isLoading}
                >
                  {t('common.save')}
                </Button>
              </Stack>
            </Stack>
          )}>
          <Form noValidate>
            <Stack direction="column" spacing={3} crossAxis="stretch">
              {emailVerificationActivated && values.email !== spaceUser.email && (
                <InfoBox color={'warning'}>
                  {t<string>('components.account.email-changed')}
                </InfoBox>
              )}

              {!!error && <InfoBox color={'danger'}>{error}</InfoBox>}

              <Field name="salutation">
                {({ form, field }) => (
                  <FormikFieldWrapper field={field.name}>
                    <RadioGroup
                      label={t<string>('common.salutation')}
                      required={!isSimplifiedRegistrationActive}
                      value={field.value}
                      onChange={e => form.setFieldValue(field.name, e.target.value)}
                    >
                      <Radio value={SALUTATIONS.madam} label={t<string>('common.madam')} />
                      <Radio value={SALUTATIONS.sir} label={t<string>('common.sir')} />
                      <Radio value={SALUTATIONS['not-specified']}
                        label={t<string>('pages.login.register.form.not-specified')} />
                    </RadioGroup>
                  </FormikFieldWrapper>
                )}
              </Field>
              {visibleFields.nickname && (
                <Field name="nickname">
                  {({ form, field }) => (
                    <FormikFieldWrapper field={field.name}>
                      <TextInput
                        label={t('common.nickname')}
                        required
                        value={field.value}
                        onChange={event => form.setFieldValue(field.name, event.target.value)}
                      />
                    </FormikFieldWrapper>
                  )}
                </Field>
              )}
              <Field name="firstname">
                {({ form, field }) => (
                  <FormikFieldWrapper field={field.name}>
                    <TextInput
                      label={t('common.firstname')}
                      required={!isSimplifiedRegistrationActive}
                      value={field.value}
                      onChange={event => form.setFieldValue(field.name, event.target.value)}
                    />
                  </FormikFieldWrapper>
                )}
              </Field>
              <Field name="lastname">
                {({ form, field }) => (
                  <FormikFieldWrapper field={field.name}>
                    <TextInput
                      label={t('common.lastname')}
                      required={!isSimplifiedRegistrationActive}
                      value={field.value}
                      onChange={event => form.setFieldValue(field.name, event.target.value)}
                    />
                  </FormikFieldWrapper>
                )}
              </Field>
              <Field name="email">
                {({ form, field }) => (
                  <FormikFieldWrapper field={field.name}>
                    <TextInput
                      type="email"
                      label={t<string>('common.email')}
                      required
                      value={field.value}
                      onChange={event => form.setFieldValue(field.name, event.target.value)}
                      disabled={spaceUser.hasSSO}
                    />
                  </FormikFieldWrapper>
                )}
              </Field>
              {visibleFields.phone && (
                <Field name="phone">
                  {({ form, field }) => (
                    <FormikFieldWrapper field={field.name}>
                      <TextInput
                        type={'tel'}
                        label={t('common.mobile-phone-number')}
                        required
                        value={field.value}
                        onChange={event => form.setFieldValue(field.name, event.target.value)}
                      />
                    </FormikFieldWrapper>
                  )}
                </Field>
              )}

              {!!(user.spaceUser?.hasSSO && ssoProviders.length) && (
                <Stack direction="column" spacing={0.5} crossAxis="stretch">
                  <InputLabel>
                    {t<string>('components.account.identity-provider')}
                  </InputLabel>

                  <Stack direction="column" spacing={0.5}>
                    {ssoProviders.map(provider => provider.provider && (
                      <Stack
                        key={provider.id}
                        fill
                        crossAxis="center"
                        align="spread"
                      >
                        <Typography as="p">
                          {t(`pages.login.index.form.sso-providers.${provider.provider}`)}
                        </Typography>
                        <RequireConfirmation
                          title={t<string>('components.account.disconnect-provider')}
                          description={t<string>('components.account.disconnect-provider-alert')}
                          confirmButton={(
                            <Button color="danger">
                              {t<string>('components.account.disconnect-provider')}
                            </Button>
                          )}
                        >
                          <Button
                            type="button"
                            variant="text"
                            disabled={isLoading}
                            onClick={() => {
                              if (provider.id) {
                                handleRemoveSSOProvider(provider.id);
                              }
                            }}
                          >
                            {t('components.account.disconnect-provider')}
                          </Button>
                        </RequireConfirmation>
                      </Stack>
                    ))}
                  </Stack>
                </Stack>
              )}
            </Stack>
          </Form>
        </Modal>
      )}
    </Formik>
  );
};

export default AccountModal;